// src/utils/linkify.tsx
// テキスト内のURL、電話番号、メールアドレスをクリッカブルリンクに変換するユーティリティ

import React from 'react';
import { isInFlutter as isInFlutterUtil } from './flutterDetection';

// Flutter WebViewからのアクセスかどうかを判定（後方互換性のため）
export const isInFlutter = (): boolean => {
  return isInFlutterUtil();
};

// FlutterCameraオブジェクトをグローバルに保存
let cachedFlutterCamera: any = null;

// 初期化時にFlutterCameraを保存
if (typeof window !== 'undefined') {
  cachedFlutterCamera = window.FlutterCamera;

  // 定期的にチェックして更新
  const checkFlutterCamera = () => {
    if (window.FlutterCamera && !cachedFlutterCamera) {
      cachedFlutterCamera = window.FlutterCamera;
      console.log('[LINKIFY] FlutterCamera cached');
    }
  };

  // 100ms, 500ms, 1000msでチェック
  setTimeout(checkFlutterCamera, 100);
  setTimeout(checkFlutterCamera, 500);
  setTimeout(checkFlutterCamera, 1000);
}

// URLを開く処理
export const openUrl = (url: string): void => {
  const inFlutter = isInFlutter();

  if (inFlutter) {
    // Flutter環境の場合
    // キャッシュされたFlutterCameraまたは現在のwindow.FlutterCameraを使用
    const flutterCamera = cachedFlutterCamera || window.FlutterCamera;

    if (flutterCamera?.postMessage) {
      const message = JSON.stringify({
        action: 'openUrl',
        url: url
      });
      flutterCamera.postMessage(message);
    } else {
      // フォールバック: _systemで開く
      window.open(url, '_system');
    }
  } else {
    // Web版の場合は別タブで開く
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

// 電話番号をダイヤルする処理
export const dialPhone = (phoneNumber: string): void => {
  if (isInFlutter()) {
    // Flutter WebViewの場合は、FlutterCameraのpostMessageを使用
    if (window.FlutterCamera?.postMessage) {
      const message = JSON.stringify({
        action: 'dial',
        phoneNumber: phoneNumber
      });
      window.FlutterCamera.postMessage(message);
    } else {
      // フォールバック: 通常のtel:リンクを試す
      window.location.href = `tel:${phoneNumber}`;
    }
  } else {
    // Web版では通常のtel:リンク
    window.location.href = `tel:${phoneNumber}`;
  }
};

// メールアドレスでメール作成画面を開く処理
export const openEmail = (email: string): void => {
  if (isInFlutter()) {
    // Flutter WebViewの場合は、FlutterCameraのpostMessageを使用
    if (window.FlutterCamera?.postMessage) {
      const message = JSON.stringify({
        action: 'email',
        email: email
      });
      window.FlutterCamera.postMessage(message);
    } else {
      // フォールバック: 通常のmailto:リンクを試す
      window.location.href = `mailto:${email}`;
    }
  } else {
    // Web版では通常のmailto:リンク
    window.location.href = `mailto:${email}`;
  }
};

// URL、電話番号、メールアドレスの正規表現パターン
const URL_PATTERN = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
const PHONE_PATTERN = /(\d{2,4}-\d{2,4}-\d{4}|\d{3}-\d{4}-\d{4}|\d{10,11})/gi;

// テキストをリンク化するコンポーネント
interface LinkifyProps {
  text: string;
  className?: string;
}

export const Linkify: React.FC<LinkifyProps> = ({ text, className }) => {
  if (!text) {
    return null;
  }

  // テキストを解析してリンク化
  const linkifyText = (inputText: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const matches: Array<{
      index: number;
      text: string;
      type: 'url' | 'email' | 'phone';
      match: string;
    }> = [];

    // URLのマッチを収集
    let match;
    URL_PATTERN.lastIndex = 0;
    while ((match = URL_PATTERN.exec(inputText)) !== null) {
      matches.push({
        index: match.index,
        text: match[1],
        type: 'url',
        match: match[1]
      });
    }

    // メールアドレスのマッチを収集
    EMAIL_PATTERN.lastIndex = 0;
    while ((match = EMAIL_PATTERN.exec(inputText)) !== null) {
      matches.push({
        index: match.index,
        text: match[1],
        type: 'email',
        match: match[1]
      });
    }

    // 電話番号のマッチを収集
    PHONE_PATTERN.lastIndex = 0;
    while ((match = PHONE_PATTERN.exec(inputText)) !== null) {
      matches.push({
        index: match.index,
        text: match[1],
        type: 'phone',
        match: match[1]
      });
    }

    // マッチを位置順でソート
    matches.sort((a, b) => a.index - b.index);

    // 重複や重なりを除去
    const filteredMatches = matches.filter((current, index) => {
      if (index === 0) return true;
      const previous = matches[index - 1];
      return current.index >= previous.index + previous.text.length;
    });

    filteredMatches.forEach((matchItem, index) => {
      // マッチ前のテキストを追加
      if (matchItem.index > lastIndex) {
        parts.push(inputText.slice(lastIndex, matchItem.index));
      }

      // リンクを作成
      const key = `link-${index}-${matchItem.type}`;
      switch (matchItem.type) {
        case 'url':
          parts.push(
            <a
              key={key}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                openUrl(matchItem.match);
              }}
              style={{
                color: '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              className={className}
            >
              {matchItem.text}
            </a>
          );
          break;
        case 'email':
          parts.push(
            <a
              key={key}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                openEmail(matchItem.match);
              }}
              style={{
                color: '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              className={className}
            >
              {matchItem.text}
            </a>
          );
          break;
        case 'phone':
          parts.push(
            <a
              key={key}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                dialPhone(matchItem.match);
              }}
              style={{
                color: '#1890ff',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              className={className}
            >
              {matchItem.text}
            </a>
          );
          break;
      }

      lastIndex = matchItem.index + matchItem.text.length;
    });

    // 残りのテキストを追加
    if (lastIndex < inputText.length) {
      parts.push(inputText.slice(lastIndex));
    }

    return parts;
  };

  const linkedText = linkifyText(text);

  return <>{linkedText}</>;
};
